<script setup lang="ts">
import { ExclamationCircleIcon } from "@heroicons/vue/24/outline";
import { usePermission } from "@vueuse/core";
import { twMerge } from "tailwind-merge";
import { ref } from "vue";

import { useUser } from "@/composables/api/useUser";

import PhotoFillIcon from "@/components/icons/PhotoFillIcon.vue";
import ProtectShieldIcon from "@/components/icons/ProtectShieldIcon.vue";
import Button from "@/components/ui/Button.vue";

const { data: user } = useUser();

const blobs = defineModel<string[]>("blobs", { required: true });
const files = defineModel<File[]>("files", { required: true });

const cameraPermission = usePermission("camera");

const fileInput = ref<HTMLInputElement | null>(null);

async function handleTakePhoto() {
  if (!fileInput.value) {
    return;
  }

  fileInput.value.click();
}

function handlePhotoCapture(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input?.files?.[0];

  if (!file) {
    return;
  }

  blobs.value.push(URL.createObjectURL(file));
  files.value.push(file);
}
</script>

<template>
  <div class="relative flex min-h-svh flex-col gap-16 px-6 pt-24 pb-6" role="region">
    <div class="mx-auto flex w-full max-w-xs flex-col gap-8">
      <div class="mx-auto flex flex-col items-center gap-4" role="banner">
        <h1
          id="camera-permission-title"
          :class="
            twMerge(
              'text-galbani text-center text-2xl font-semibold',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
        >
          {{ $t("cameraPermission.title") }}
        </h1>

        <div
          :class="
            twMerge(
              'text-galbani relative',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
          role="img"
          aria-label="Icône d'appareil photo avec badge de protection"
        >
          <PhotoFillIcon class="size-24" aria-hidden="true" />

          <ProtectShieldIcon class="absolute -right-2.5 -bottom-0.5 size-8" aria-hidden="true" />
        </div>
      </div>

      <div class="mx-auto flex w-full items-center gap-3" role="alert" aria-live="polite">
        <ExclamationCircleIcon
          :class="
            twMerge(
              'fill-topchef text-galbani size-7 shrink-0',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue fill-lactel-yellow',
            )
          "
          aria-hidden="true"
        />

        <p class="text-secondary text-base leading-tight">
          {{ $t("cameraPermission.description") }}
        </p>
      </div>

      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        capture="environment"
        class="hidden"
        @change="handlePhotoCapture"
      />

      <Button
        variant="secondary"
        class="w-full"
        aria-label="Autoriser l'accès à l'appareil photo"
        @click="handleTakePhoto"
      >
        {{ $t("cameraPermission.button") }}
      </Button>

      <p v-if="cameraPermission === 'denied'" class="text-sm text-red-700">
        {{ $t("cameraPermission.deniedWarning") }}
      </p>
    </div>
  </div>
</template>
